import { useMemo } from "react";

import { useAppContext } from "@/app/context";
import { useRecOptions } from "@/app/hooks";
import { formatAttributes } from "@/app/lib/format-attributes";
import { RECOrderAttributeOptions } from "@/app/lib/format-rec-options";
import { RECsHoldingsUnSupportedColumns } from "@/app/pages/recs/rec-holdings";
import {
  ErcotRecAttributes,
  ExportDetailsQuery,
  MretsRecAttributes,
  useExportDetailsQuery,
} from "@/app/types/generated/graphql";
import { TraceXSupportedRegistries } from "@/app/types/okta.types";

import { UseExportRequestDetailsFn } from "./export-history-details-modal.types";

export const getExportHistoryDetailsData = (
  data: ExportDetailsQuery["exportRequestById"]["exportRequestDetails"],
  recOptions?: RECOrderAttributeOptions,
) =>
  !recOptions
    ? []
    : data?.map((detail) => ({
        ...detail,
        //Add as MretsRecAttributes | ErcotRecAttributes during Ercot Export integration
        attributes: detail.attributes as MretsRecAttributes | ErcotRecAttributes,
        ...formatAttributes({
          attributes: detail.attributes as MretsRecAttributes | ErcotRecAttributes,
          options: recOptions,
        }),
      })) ?? [];

export const useExportRequestDetails: UseExportRequestDetailsFn = ({ id = "" }) => {
  const { recOptions, loading: recOptionsLoading } = useRecOptions();
  const { userSelections } = useAppContext();
  const activeRegistryCode = userSelections?.registry?.registryCode as TraceXSupportedRegistries;

  const { data, loading: queryLoading } = useExportDetailsQuery({
    variables: {
      id,
    },
  });

  const loading = queryLoading ?? recOptionsLoading;

  const exportRequestDetailsHiddenColumns = useMemo(() => {
    const { unsupportedColumns = [], toggleColumns = [] } = RECsHoldingsUnSupportedColumns[activeRegistryCode];
    return [...unsupportedColumns, ...toggleColumns];
  }, [activeRegistryCode]);

  return {
    recOptions,
    loading,
    exportData: data?.exportRequestById,
    exportRequestDetailsHiddenColumns,
    activeRegistryCode,
  };
};
